import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import {
  IconAdjustmentsHorizontal,
  IconHash,
  IconSettings,
} from "@tabler/icons-react";
import NavRoomsIcon from "../../assets/icons/nav/nav-rooms.svg?react";
import {
  NewContentDot,
  TAB_BAR_LAYOUTS,
  TabButton,
  type TabBarLayout,
} from "../ui/TabButton";
import Skeleton from "../skeleton/Skeleton";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { ScrollArrowButton } from "../ui/ScrollArrowButton";
import { useHorizontalOverflow } from "../../hooks/use-horizontal-overflow";
import { MeshFilterDrawer, type PracticeFeedFilters } from "./MeshFilterDrawer";
import type { PracticeRailData } from "../../domains/practice/hooks/usePracticeRailTerms";
import { usePracticeFilterValues } from "../../domains/practice/hooks/usePracticeFilterValues";
import type { PracticeRailCorpus } from "../../domains/practice/hooks/usePracticeRailTerms";

/**
 * What the rail can have selected.
 *
 * There is NO `practice` member. A practice is the CORPUS being read, chosen in
 * the header dropdown, and it is not a filter: it never competes with a chip,
 * "Clear all" never clears it, and it does not accumulate with what is here. The
 * member it replaced was one practice per chip, which put the corpus and the
 * filters on one row and made the widest thing on screen the one you could not
 * remove.
 *
 * The two members left are the two things a chip can be. `mesh` narrows the
 * practice to the publications a descriptor is a MAJOR topic of; `room` leaves
 * the practice corpus altogether for an editorial one. Mutually exclusive
 * because they are different feeds, not two filters of one — and the page owns
 * the URL encoding of both.
 */
type PracticeTabSelection =
  { kind: "room"; roomId: string } | { kind: "mesh"; descriptorUi: string };

type PracticeFeedRailProps = {
  rail: PracticeRailData;
  rooms: ReadonlyArray<{ id: string; name: string; badge: boolean | null }>;
  /** Which chip is lit. `null` is no chip, which is NOT the same as no filter. */
  selection: PracticeTabSelection | null;
  /**
   * A chip tap. `null` is a re-tap on the live chip, which widens the view and
   * leaves the drawer's two axes alone.
   */
  onSelect: (selection: PracticeTabSelection | null) => void;
  /**
   * The drawer's two axes, which do not appear on this row: they are
   * multi-select, and a bar of exclusive chips cannot hold them. They live here
   * rather than in the drawer because THIS is where the count is derived, so the
   * badge and the panel header cannot disagree.
   */
  filters: PracticeFeedFilters;
  onChangeFilters: (filters: PracticeFeedFilters) => void;
  /** Every filter off, the practice untouched. */
  onClearAll: () => void;
  /**
   * WHICH corpus the drawer describes, and whether there is one at all.
   *
   * `null` is Room mode: an editorial corpus with none of these axes, and no
   * drawer. `{ kind: "all" }` is a corpus with a MeSH chip and no axis values —
   * nothing counts journals or publication types over a union — which is a
   * drawer holding the live term and "Clear all", and NOT a reason to withhold
   * one. The pinned chip is the exit that never scrolls, so the aggregate is the
   * last surface to take it away from: its rail is merged from up to six
   * practices, so it is the one most likely to be long enough to scroll a
   * selected chip out of sight.
   */
  drawerCorpus: PracticeRailCorpus | null;
  /** Named in the drawer's footer, which states that filters never clear it. */
  practiceLabel: string;
  layout: TabBarLayout;
};

function RoomFilledIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="13.5" y="14" width="7" height="7" rx="1" />
      <rect x="2.5" y="14" width="7" height="7" rx="1" />
      <rect x="2.5" y="3" width="7" height="7" rx="1" />
      <rect
        x="16.9834"
        y="2.41421"
        width="6.25"
        height="6.25"
        rx="1"
        transform="rotate(45 16.9834 2.41421)"
      />
    </svg>
  );
}

/**
 * The width of a chip, and the bar's whole affordance.
 *
 * Uncapped, one long chip fills a phone bar and the reader has no way to learn
 * that more sit off screen: the bar hides its scrollbar, and nobody tries a
 * horizontal swipe on something that reads as a title. Capped, at least one
 * further chip is always partially visible whatever the labels are and however
 * many there are, which is the one property that makes the bar readable as a
 * list.
 *
 * It used to bite HARDER than on practice names: raw MeSH labels are
 * comma-inverted noun phrases ("Carcinoma, Non-Small-Cell Lung", "Thoracic
 * Surgery, Video-Assisted") and the first chip truncated mid-phrase. That was
 * fixed at the source, as this comment said it would be —
 * `domain/mesh/descriptor-labels.ts` curates a display label per descriptor
 * (`NSCLC`, `VATS`) and the endpoint resolves it. Truncation is NOT gone and was
 * never going to be: this box is 140 px of text, about 20 characters of DM Sans
 * 600 at 14px (measured here, in this component). What changed is how often —
 * over the 665 descriptors a rail reaches, the raw NLM name fitted 59.1% of the
 * time and the curated English label fits 78.9%. Widening the cap would cost the
 * affordance above; the rest is the accepted floor.
 */
const CHIP_MAX = "max-w-48";

/**
 * How much of the chip AFTER the selected one stays in sight once the row has
 * scrolled: the row's own 8px gap plus enough of the next chip to read as a chip
 * rather than as a clipped edge. Tapping the second chip has to bring the third
 * into sight, or the bar teaches the reader they have seen everything.
 */
const NEXT_CHIP_PEEK = 48;

/**
 * The pinned chip. It NEVER scrolls, which is why it — and not the active chip —
 * carries the exit: the row scrolls, so anything living inside a chip can leave
 * the viewport. Mobile draws `IconAdjustmentsHorizontal` rather than a gear,
 * because a gear promises settings; the Figma desktop chip draws the gear.
 *
 * On mobile, the badge counts the whole filter state. The desktop chip is a
 * plain 32px square with no badge, as in Figma; the drawer header still shows
 * the count.
 */
function DrawerChip({
  activeFilterCount,
  onOpen,
  layout,
}: {
  activeFilterCount: number;
  onOpen: () => void;
  layout: TabBarLayout;
}): React.JSX.Element {
  if (layout === "desktop")
    return (
      <button
        type="button"
        aria-label="Filters"
        onClick={onOpen}
        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-ds-slate-50 text-ds-mint-950"
      >
        <IconSettings size={16} stroke={TAB_BAR_LAYOUTS.desktop.iconStroke} />
      </button>
    );
  return (
    <button
      type="button"
      aria-label="Filters"
      onClick={onOpen}
      className={cn(
        "flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-ds-slate-50 text-ds-mint-950",
        activeFilterCount === 0 ? "w-10" : "gap-1.5 px-3"
      )}
    >
      <IconAdjustmentsHorizontal size={20} stroke={1.6} />
      {activeFilterCount === 0 ? null : (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-ds-primary px-1 text-caption font-bold text-ds-on-primary">
          {activeFilterCount}
        </span>
      )}
    </button>
  );
}

/**
 * The scrolling row, and the only thing here that touches the DOM. It scrolls by
 * the minimum that keeps the selected chip whole AND leaves `NEXT_CHIP_PEEK` of
 * the chip after it.
 *
 * ── What this no longer does, deliberately ──────────────────────────────────
 * It used to MEASURE the selected chip's line boxes and pin its label to the
 * widest, twice — once immediately and once after `document.fonts.ready`. All of
 * that existed because a practice chip WRAPPED to two lines when selected
 * (`max-w-4/5`, `line-clamp-2`, `text-balance`): a wrapped box keeps the width it
 * was handed, so the shorter line's slack stayed as ~85px of empty chip, and no
 * CSS width keyword gives it back.
 *
 * A MeSH chip truncates, it never wraps, so its width does not change on
 * selection and there is nothing to measure or pin — and with the practice out of
 * the rail, no chip here has to be readable in full: the corpus name lives in the
 * header dropdown, and a term the reader cannot fully read is still removable
 * from the pinned chip. That deletes the measurement, the webfont re-measure, the
 * `data-chip-label` hook and the wrap/height apparatus that kept the row from
 * reading as two rows of different things. Rooms lose the wrap with everything
 * else rather than keeping the whole machine alive for a minority of chips.
 *
 * `termsKey` is in the deps because the rail is a property of the PRACTICE: a new
 * practice is a new list, and a scroll position kept across the change opened the
 * new rail on its fourth term (found in the proto). With nothing selected the row
 * returns to its head, which is also what clearing the filter should do. Between
 * two runs the reader's own scrolling is never overridden.
 *
 * `aria-pressed` is how it finds the chip: one query beats a ref per chip. The
 * row scrolls itself rather than calling `scrollIntoView`, which would also move
 * the page. `scroll-fog-x` fades whichever edge has content behind it — a nicety
 * and NOT the affordance, since it needs scroll-driven animations and degrades to
 * nothing; `CHIP_MAX` and the peek are what carry it in every browser.
 *
 * On desktop, the Figma "next" `IconButton` follows the row while chips hide
 * past its end. It scrolls one visible width, less `NEXT_CHIP_PEEK`, so the
 * chip that was cut at the edge comes back whole.
 */
function ChipRow({
  termsKey,
  selectedKey,
  layout,
  children,
}: {
  termsKey: string;
  selectedKey: string | null;
  layout: TabBarLayout;
  children: ReactNode;
}): React.JSX.Element {
  const rowRef = useRef<HTMLDivElement>(null);
  const overflows = useHorizontalOverflow(rowRef);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (row === null) return;

    const settleSelectedChip = (): void => {
      const chip = row.querySelector('[aria-pressed="true"]');
      if (chip === null) {
        row.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }

      const chipBox = chip.getBoundingClientRect();
      const start =
        chipBox.left - row.getBoundingClientRect().left + row.scrollLeft;
      const end =
        start +
        chipBox.width +
        (chip.nextElementSibling === null ? 0 : NEXT_CHIP_PEEK);
      const visible = row.clientWidth;
      const revealed =
        end > row.scrollLeft + visible ? end - visible : row.scrollLeft;
      // The start correction is applied second on purpose: a chip too wide for
      // the bar to hold with its peek keeps its first word rather than its last.
      // Rounded away from whichever edge it protects, or a subpixel position
      // clips the pixel it was computed to save.
      row.scrollTo({
        left: start < revealed ? Math.floor(start) : Math.ceil(revealed),
        behavior: "smooth",
      });
    };

    settleSelectedChip();

    // Run again once the webfont lands, and NOT for the reason the practice bar
    // ran twice. That version re-MEASURED the selected label, which a MeSH chip
    // has no need of. But every number above comes out of `getBoundingClientRect`
    // on text: the fallback face is narrower, so a target computed against it
    // leaves the chip short of where it was meant to sit — and a chip at the
    // right edge short of its own width is a chip half off screen, which is
    // exactly what the scroll exists to prevent.
    if (document.fonts.status === "loaded") return;
    let mounted = true;
    void document.fonts.ready.then(() => {
      if (mounted) settleSelectedChip();
    });
    return () => {
      mounted = false;
    };
  }, [termsKey, selectedKey]);

  const row = (
    <div
      ref={rowRef}
      className={cn(
        "scroll-fog-x flex min-w-0 flex-1 items-center gap-2 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden",
        TAB_BAR_LAYOUTS[layout].rowEnd
      )}
    >
      {children}
    </div>
  );
  if (layout === "mobile") return row;
  return (
    <>
      {row}
      {overflows ? (
        <ScrollArrowButton
          direction="next"
          onClick={() => {
            const current = rowRef.current;
            if (current === null)
              throw new Error("should not happen: the chip row is unmounted");
            current.scrollBy({
              left: current.clientWidth - NEXT_CHIP_PEEK,
              behavior: "smooth",
            });
          }}
        />
      ) : null}
    </>
  );
}

/** What the row's scroll depends on: WHICH chip is pressed, and nothing else. */
function selectedChipKey(
  selection: PracticeTabSelection | null
): string | null {
  if (selection === null) return null;
  switch (selection.kind) {
    case "mesh":
      return `mesh:${selection.descriptorUi}`;
    case "room":
      return `room:${selection.roomId}`;
    default:
      throw new Error(selection satisfies never);
  }
}

/**
 * The rail: the MeSH descriptors the reader's current practice is about, plus
 * their Rooms, behind one pinned filter chip.
 *
 * Rooms STAY. A Room is an editorial subscription, not a statement about what the
 * reader practises — replacing the relevance source underneath the feed is no
 * reason to take a B2B reader's rooms off the surface they reach them from.
 */
function PracticeFeedRail({
  rail,
  rooms,
  selection,
  onSelect,
  filters,
  onChangeFilters,
  onClearAll,
  drawerCorpus,
  practiceLabel,
  layout,
}: PracticeFeedRailProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const style = TAB_BAR_LAYOUTS[layout];
  // The disclosure state of the pinned chip's own panel, and the reason it lives
  // HERE rather than in the page: the chip opens it, the chip's badge counts what
  // is in it, and nothing outside this row can open or close it. Threading it
  // through the page would give two components a say in one boolean.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // A 501 means the practice Boolean did not compile, so the feed under the rail
  // is dead too. Offering filters over a feed that does not exist would be a lie,
  // and one failure states itself once: the MeSH half leaves the screen — the
  // chips AND the pinned chip, since there is nothing left to filter — and the
  // feed does the talking. Rooms are the one thing that outlives it: they are a
  // different corpus, they still work, and dropping a reader's Rooms because
  // their practice failed to compile would remove navigation this failure never
  // touched.
  const meshSupported = rail.status === "ready";
  const terms = rail.status === "ready" ? rail.terms : [];

  // ONE count, derived where the badge and the drawer header both read it, so
  // they cannot disagree. Three axes now: the rail's chip counts as one, and each
  // ticked box in the drawer counts as one more.
  const selectedTerm =
    selection === null || selection.kind !== "mesh"
      ? null
      : terms.find((term) => term.descriptorUi === selection.descriptorUi);
  const liveTerm = selectedTerm === undefined ? null : selectedTerm;

  // Fired by OPENING the panel, never with the page: answering it costs another
  // evaluation of the practice's matched set, and most readers never open it.
  // Scoped to the live chip, so the values describe what is actually on screen.
  //
  // Off on the aggregate too, and not merely empty: `:uid/filter-values` counts
  // over ONE practice's matched set, so there is no request to make. The hook
  // answers `ready` with two empty lists, which is what the drawer renders as no
  // axis at all.
  //
  // ⚠️ THE LAST HOOK, and it stays ABOVE every early return below. It sat under
  // them once and crashed the surface on the first render that got past the
  // skeleton: the rail arrives `loading`, the component returns early with two
  // hooks, then the terms land and it runs three. React counts hooks, not
  // branches. `null` is what turns the query off — never an early return.
  const filterValues = usePracticeFilterValues(
    !drawerOpen || drawerCorpus === null || drawerCorpus.kind !== "practice"
      ? null
      : {
          practiceUid: drawerCorpus.uid,
          descriptorUi: liveTerm === null ? null : liveTerm.descriptorUi,
        }
  );

  if (rail.status === "loading")
    return <PracticeFeedRailSkeleton layout={layout} />;

  const activeFilterCount =
    (liveTerm === null ? 0 : 1) +
    filters.journalUids.length +
    filters.publicationTypes.length;

  const roomChips = rooms.map((room) => {
    const selected =
      selection !== null &&
      selection.kind === "room" &&
      selection.roomId === room.id;
    return (
      <TabButton
        key={room.id}
        className={cn(CHIP_MAX, style.chip)}
        active={selected}
        icon={
          <>
            {room.badge === true && <NewContentDot />}
            <NavRoomsIcon className="size-4 shrink-0" />
          </>
        }
        activeIcon={
          <>
            {room.badge === true && <NewContentDot />}
            <RoomFilledIcon className="size-4 shrink-0 text-ds-slate-50" />
          </>
        }
        onClick={() => {
          onSelect({ kind: "room", roomId: room.id });
        }}
      >
        <span className="min-w-0 truncate">{room.name}</span>
      </TabButton>
    );
  });

  // Nothing to say at all: an unsupported practice for a reader with no Room.
  // The proto's own outcome, reached here only when Rooms cannot rescue the row.
  if (!meshSupported && roomChips.length === 0) return null;

  // The pinned chip appears whenever a PRACTICE is being read AND its Boolean
  // compiles. It no longer waits for the rail to find terms: journals and
  // publication types are filterable even where no descriptor stands out, so a
  // thin corpus now has a drawer with something in it.
  //
  // Two things still hide it. ROOM MODE, because a Room is an editorial corpus
  // with none of these axes — `drawerCorpus` is `null` there, which also switches
  // the query below off. And the 501, because a practice whose Boolean has no SQL
  // reading has no values to offer either: the whole MeSH half leaves the screen,
  // this chip included, and the feed says what happened once.
  //
  // "All practices" is NOT one of them. It has no axis values, and the drawer
  // draws an axis only where there are values to draw, so it opens on the live
  // term and "Clear all" — one removable thing, which is one more exit than the
  // scrolling row can promise.
  const showDrawerChip = drawerCorpus !== null && meshSupported;

  // A legitimate answer, and the one that needs saying out loud: a corpus too
  // thin to over-represent any descriptor. It is also the failure mode a
  // mandatory screen would otherwise present as a success. Said only when there
  // is nothing else on the row — with Rooms beside it the row is not empty, and
  // in Room mode there is no practice on screen to say it about.
  const sayEmptyRail =
    rail.status === "ready" &&
    rail.terms.length === 0 &&
    roomChips.length === 0 &&
    selection === null;

  return (
    // The mobile `pl-5` sits on the ROW and not on the pinned chip: the inset is
    // the screen's, so a row without that chip (Room mode, a 501 with Rooms to
    // save it) must not start flush against the edge. Desktop has no inset: the
    // row starts on the feed column's edge.
    <div className={cn("flex items-center gap-2 pt-3", style.rowStart)}>
      {showDrawerChip && (
        <div className="shrink-0">
          <DrawerChip
            layout={layout}
            activeFilterCount={activeFilterCount}
            onOpen={() => {
              setDrawerOpen(true);
            }}
          />
        </div>
      )}
      {sayEmptyRail ? (
        <p
          className={cn(
            "m-0 min-w-0 text-footnote text-ds-slate-500",
            style.rowEnd
          )}
        >
          {t("No MeSH filter for this practice yet.")}
        </p>
      ) : (
        <ChipRow
          // The rail belongs to the PRACTICE, so a new list is a new practice and
          // the row goes back to its head. Rooms are not in the key: they are the
          // same Rooms whichever practice is being read.
          termsKey={terms.map((term) => term.descriptorUi).join(",")}
          selectedKey={selectedChipKey(selection)}
          layout={layout}
        >
          {terms.map((term) => {
            const selected =
              selection !== null &&
              selection.kind === "mesh" &&
              selection.descriptorUi === term.descriptorUi;
            return (
              <TabButton
                key={term.descriptorUi}
                className={cn(CHIP_MAX, style.chip)}
                active={selected}
                icon={
                  <IconHash
                    size={16}
                    stroke={style.iconStroke}
                    className="shrink-0"
                  />
                }
                onClick={() => {
                  // Re-tap on the live chip clears it. The design forbids a
                  // cross ON the chip (the pinned counter carries the exit), and
                  // a dead tap would be worse than either.
                  onSelect(
                    selected
                      ? null
                      : { kind: "mesh", descriptorUi: term.descriptorUi }
                  );
                }}
              >
                <span className="min-w-0 truncate">{term.label}</span>
              </TabButton>
            );
          })}
          {roomChips}
        </ChipRow>
      )}
      {showDrawerChip && (
        <MeshFilterDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          activeFilterCount={activeFilterCount}
          practiceLabel={practiceLabel}
          selectedTerm={liveTerm}
          // Clearing the term WIDENS the view, and a wider view holds at least
          // every journal and type the narrow one did — so the ticked boxes
          // survive it. Selecting a term is the direction that has to clear them,
          // and the page owns that rule.
          onClearTerm={() => {
            onSelect(null);
          }}
          values={filterValues}
          filters={filters}
          onChangeFilters={onChangeFilters}
          onClearAll={onClearAll}
        />
      )}
    </div>
  );
}

// Representative widths: which terms land first is exactly what is not known yet.
const SKELETON_CHIP_WIDTHS = [148, 96, 132] as const;

/**
 * Loading silhouette: the pinned chip collapsed to a square, then filter chips
 * of the same height with an 8px (`rounded-lg`) radius, not pills, sharing the
 * real row's inset.
 */
function PracticeFeedRailSkeleton({
  layout,
}: {
  layout: TabBarLayout;
}): React.JSX.Element {
  const { chipHeight, rowStart, rowEnd } = TAB_BAR_LAYOUTS[layout];
  return (
    <div className={cn("flex items-center gap-2 pt-3", rowStart, rowEnd)}>
      <Skeleton className="rounded-lg" width={chipHeight} height={chipHeight} />
      {SKELETON_CHIP_WIDTHS.map((width) => (
        <Skeleton
          key={width}
          className="rounded-lg"
          width={width}
          height={chipHeight}
        />
      ))}
    </div>
  );
}

export {
  PracticeFeedRail,
  type PracticeFeedRailProps,
  type PracticeTabSelection,
};
